import { ClarityEventName, ClarityTagKey, ClarityUpgradeReason } from "./clarityEvents";

/**
 * Defensive cap on custom tag values. Clarity's web API does not publish a
 * limit, but its mobile SDKs reject values beyond 255 characters, so long
 * values are truncated rather than risked. Every tag we send is far shorter;
 * this only guards against unexpected CRM data.
 */
const MAX_TAG_VALUE_LENGTH = 255;

/**
 * Origins where the Clarity tag is never injected but instrumentation still
 * needs to be verifiable. Mirrors `useIsDevOrStaging` in `windowHelpers`.
 */
const DEBUG_ORIGINS = new Set<string>([
  "https://lemon-staging.xbox.com",
  "https://lemon-int-pme.xbox.com",
  "https://onboarding.dev.microsoft.com:3000",
]);

type ClarityGlobal = (...args: unknown[]) => void;

/**
 * Resolve the Clarity global, or undefined when it is not available.
 *
 * There are two entirely normal cases where this returns undefined, and
 * neither is an error:
 *
 * 1. Server-side rendering — `ClarityInitializer` is mounted in both the
 *    client and server entry points, so this module is evaluated during SSR
 *    where there is no `window` at all.
 * 2. Consent denied, or a non-production origin — `useCookieConsent` only
 *    injects the Clarity script on the production host and only once the WCP
 *    `Analytics` category is allowed. Until then (and forever, if the user
 *    declines) the global never exists.
 *
 * Every call in this module funnels through `callClarity` so that Clarity
 * calls silently no-op instead of throwing.
 */
const getClarity = (): ClarityGlobal | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const candidate = (window as Window & { clarity?: unknown }).clarity;

  return typeof candidate === "function" ? (candidate as ClarityGlobal) : undefined;
};

/**
 * True on dev and staging hosts, where the Clarity tag is deliberately not
 * loaded. Instrumentation is echoed to the console there so that call sites
 * can be verified before they reach production, without sending
 * non-production traffic into the live Clarity project.
 */
const isDebugOrigin = (): boolean =>
  typeof window !== "undefined" && DEBUG_ORIGINS.has(window.location?.origin);

/** True when Clarity has actually been loaded and is accepting calls. */
export const isClarityAvailable = (): boolean => getClarity() !== undefined;

const callClarity = (...args: [string, ...Array<unknown>]): void => {
  const clarity = getClarity();

  if (clarity) {
    clarity(...args);
    return;
  }

  if (isDebugOrigin()) {
    // eslint-disable-next-line no-console
    console.debug("[clarity]", ...args);
  }
};

/**
 * Emit a custom event.
 *
 * Clarity events carry no properties — the name is the whole payload. Use
 * custom tags for any dimension you want to segment by.
 */
export const trackClarityEvent = (eventName: ClarityEventName): void => {
  callClarity("event", eventName);
};

/**
 * Emit several custom events in one call.
 *
 * The landing page CTAs use this to fire a placement-specific event alongside
 * the aggregate `landingCtaClicked` event.
 */
export const trackClarityEvents = (...eventNames: Array<ClarityEventName>): void => {
  eventNames.forEach(eventName => callClarity("event", eventName));
};

/**
 * Set a custom tag used to segment sessions and recordings.
 *
 * Empty, null and undefined values are skipped: Clarity has no concept of
 * clearing a tag, so writing an empty string would create a meaningless
 * segment rather than leaving the dimension unset.
 */
export const setClarityTag = (
  key: ClarityTagKey,
  value: string | number | boolean | undefined | null
): void => {
  if (value === undefined || value === null) {
    return;
  }

  const stringValue = String(value);

  if (!stringValue) {
    return;
  }

  callClarity("set", key, stringValue.slice(0, MAX_TAG_VALUE_LENGTH));
};

export type ClarityTagValues = Partial<
  Record<ClarityTagKey, string | number | boolean | undefined | null>
>;

/**
 * Set several custom tags at once. Entries with no value are skipped.
 */
export const setClarityTags = (tags: ClarityTagValues): void => {
  (Object.entries(tags) as Array<[ClarityTagKey, ClarityTagValues[ClarityTagKey]]>).forEach(
    ([key, value]) => setClarityTag(key, value)
  );
};

/**
 * Associate the current session with an account.
 *
 * `customId` must stay a non-personal opaque identifier (the CRM account
 * GUID). `friendlyName` is rendered in the Clarity dashboard in plaintext in
 * place of the hashed id, which is what makes the recordings usable by a
 * human — they see the studio name rather than a GUID.
 *
 * Clarity's documentation specifies this should be called on every page rather
 * than once per session.
 */
export const identifyClarityUser = (
  customId: string | undefined | null,
  friendlyName?: string
): void => {
  if (!customId) {
    return;
  }

  // sessionId and pageId are intentionally omitted so Clarity derives its own.
  callClarity("identify", customId, undefined, undefined, friendlyName);
};

/**
 * Prioritize the current session so it survives Clarity's daily sampling cap.
 *
 * Reserve this for rare, high-value sessions — upgrading everything defeats
 * the purpose of prioritization.
 */
export const upgradeClaritySession = (reason: ClarityUpgradeReason): void => {
  callClarity("upgrade", reason);
};

/**
 * Attribute to place on any element rendering partner data as read-only text.
 *
 * Clarity masks form input, numbers and email addresses out of the box, but it
 * does not mask text that the application has already rendered into the DOM.
 * Spread this onto those containers.
 */
export const clarityMaskProps = { "data-clarity-mask": "true" } as const;
